/**
 * Какие выстрелы сейчас «в полете»: залп на поле еще не попал в цель, и итог
 * выстрела придержан (см. HexGridRenderer.showShotAnimation).
 *
 * Знает об этом рендерер поля (Phaser), а нужно это и интерфейсу (React):
 * панель с результатом стрельбы в футере не должна открываться раньше, чем
 * итог появится на поле, — иначе она закрывает отряд-цель, по которому как
 * раз идет анимация (см. useShootingResultRevealed).
 *
 * ИНТЕРФЕЙС ЖДЕТ ЧУТЬ ДОЛЬШЕ ПОЛЯ. Попадание открывает итог на поле сразу, а
 * панель в футере — через паузу (revealDelayMs у markShotLanded): урон на
 * цели и линию выстрела сначала видно целиком, и только потом низ поля
 * закрывает панель. Отсюда два вопроса: летит ли залп (checkIfShotInFlight) и
 * придержан ли итог для интерфейса (checkIfShotResultHeld — летит либо только
 * что упал).
 *
 * Отдельный маленький источник с подпиской, а не redux: это состояние
 * анимации, а не партии. В записи матча и по сети ему не место, а диспатч
 * ради него перерисовывал бы поле лишний раз.
 */
const shotsInFlight = new Set();
// Залп упал, пауза перед показом итога в интерфейсе еще идет: id выстрела → таймер
const settlingShots = new Map();
const listeners = new Set();

const notify = () => {
  // Копия: слушатель может отписаться прямо из своего вызова
  [...listeners].forEach((listener) => listener());
};

const stopSettling = (actionId) => {
  if (!settlingShots.has(actionId)) {
    return false;
  }
  clearTimeout(settlingShots.get(actionId));
  settlingShots.delete(actionId);
  return true;
};

/** Залп выстрела ушел: его итог придержан до попадания */
export const markShotInFlight = (actionId) => {
  if (!actionId || shotsInFlight.has(actionId)) {
    return;
  }
  // Переброс костей сразу за попаданием: пауза прежнего залпа больше не нужна
  stopSettling(actionId);
  shotsInFlight.add(actionId);
  notify();
};

/**
 * Залп попал в цель (или оборван): итог выстрела виден на поле.
 *
 * @param {string} actionId
 * @param {Object} [options]
 * @param {number} [options.revealDelayMs] — пауза, после которой итог
 *   открывается и интерфейсу. Не задана (оборванный залп) — сразу
 */
export const markShotLanded = (actionId, {revealDelayMs = 0} = {}) => {
  // Залп не летел: упал раньше (пауза уже идет, ее не трогаем) либо его не было
  if (!shotsInFlight.delete(actionId)) {
    return;
  }
  if (revealDelayMs > 0) {
    settlingShots.set(actionId, setTimeout(() => {
      settlingShots.delete(actionId);
      notify();
    }, revealDelayMs));
  }
  notify();
};

export const checkIfShotInFlight = (actionId) => shotsInFlight.has(actionId);

/** Летит ли хоть один залп (автовыбор отрядов ждет попадания, см. HexGridRenderer.isFieldActionAnimating) */
export const checkIfAnyShotInFlight = () => shotsInFlight.size > 0;

/** Придержан ли итог выстрела для интерфейса: залп летит либо только что упал */
export const checkIfShotResultHeld = (actionId) => shotsInFlight.has(actionId) || settlingShots.has(actionId);

/**
 * Подписка на перемены: вылет залпа, попадание, конец паузы после попадания
 * @returns {Function} отписка
 */
export const subscribeToShotsInFlight = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Забыть все залпы (тесты) */
export const resetShotsInFlight = () => {
  if (shotsInFlight.size === 0 && settlingShots.size === 0) {
    return;
  }
  shotsInFlight.clear();
  [...settlingShots.keys()].forEach(stopSettling);
  notify();
};
